import type { DeviceViewSettingsTable } from '@ipfabric/utils';
import type { SelectedTables } from 'components/DeviceView/deviceViewTypes';
import { type Draft, produce } from 'immer';
import { type FC, Fragment, memo, useMemo, useState } from 'react';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { dragTypes } from 'components/DeviceView/deviceViewConstants';
import {
  type MenuTreeNode,
  type PageTitleTranslations,
  isPageItem,
} from 'components/Sidebar/menuTree';
import type { RoutesWithApiEndpoint } from 'containers/App/routing/RoutesWithApiEndpoint';
import { useDrag } from 'react-dnd';

interface Open {
  [key: string]: boolean;
}

interface TreeRowProps {
  data: MenuTreeNode[];
  onAddTable: (data: SelectedTables) => void;
  onOpenChange: (data: Open) => void;
  open: Open;
  routesWithApiEndpoint: RoutesWithApiEndpoint;
  selectedTables: SelectedTables;
  tree: MenuTreeNode[];
}

interface DraggableTreeItemProps {
  msg: PageTitleTranslations;
  onAddTable: (data: SelectedTables) => void;
  path: string[];
  selectedTables: SelectedTables;
  webEndpoint: string;
}

interface ListNameProps {
  item: MenuTreeNode;
  itemKey: string;
  onOpenChange: (data: Open) => void;
  open: Open;
}

const DraggableTreeItem: FC<DraggableTreeItemProps> = (props) => {
  const label = (props.msg.boxTitle || props.msg.pageTitle) ?? '';

  const isSelectedWebEndpoint = useStableCallback(
    (table: DeviceViewSettingsTable): boolean =>
      table.webEndpoint === props.webEndpoint,
  );

  const tableSelected = useMemo(
    () => props.selectedTables.some(isSelectedWebEndpoint),
    [props.selectedTables],
  );

  const path = useMemo(() => {
    if (props.path) {
      return `(${props.path.join(' - ')})`;
    }
    return '';
  }, [props.path]);

  const handleAddTable = useStableCallback(() => {
    const foundTable = props.selectedTables.some(isSelectedWebEndpoint);
    if (!foundTable) {
      props.onAddTable(
        produce(props.selectedTables, (draft: Draft<SelectedTables>): void => {
          draft.push({
            label,
            path,
            webEndpoint: props.webEndpoint,
          });
        }),
      );
    }
  });

  const [, drag] = useDrag({
    item: {
      label,
      path,
      type: dragTypes.TREE,
      webEndpoint: props.webEndpoint,
    },
    canDrag() {
      return !tableSelected;
    },
  });

  return (
    <li
      className={classNames(
        'device-view-setup-tables__tree-item ml-lgu-3 whitespace-nowrap',
        tableSelected ? 'text-disabled' : 'cursor-move',
      )}
      onDoubleClick={handleAddTable}
      ref={drag}
    >
      <Button
        variant='tertiary'
        size='xxs'
        icon='fa-circle-plus'
        disabled={tableSelected}
        className='mr-lgu-3'
        onClick={handleAddTable}
        type='button'
      />
      {label}
    </li>
  );
};

const ListName: FC<ListNameProps> = (props) => {
  const isExpandable = props.item.children && props.item.children.length > 0;

  const handleOpenChange = useStableCallback(() => {
    if (!isExpandable) {
      return;
    }

    props.onOpenChange(
      produce(props.open, (draft: Draft<Open>) => {
        draft[props.itemKey] = !props.open[props.itemKey];
      }),
    );
  });

  return (
    <li
      className={classNames(
        'device-view-setup-tables__tree-item',
        isExpandable && 'cursor-pointer',
      )}
      onClick={handleOpenChange}
    >
      {isExpandable && (
        <i
          className={classNames(
            'device-view-setup-tables__tree-item-caret fa',
            props.open[props.itemKey] ? 'fa-caret-down' : 'fa-caret-right',
          )}
        />
      )}
      <i className='device-view-setup-tables__tree-item-folder fa fa-folder-o' />
      {props.item.label}
    </li>
  );
};

const TreeRow: FC<TreeRowProps> = (props) => {
  const rows = props.data.map((item: MenuTreeNode, index: number) => {
    const key = `${index} ${item.label}`;

    return (
      <Fragment key={key}>
        <ListName
          item={item}
          itemKey={key}
          open={props.open}
          onOpenChange={props.onOpenChange}
        />
        {props.open[key] && item.children && item.children.length > 0 && (
          <li className='pl-10'>
            <TreeRow
              data={item.children}
              onAddTable={props.onAddTable}
              onOpenChange={props.onOpenChange}
              open={props.open}
              routesWithApiEndpoint={props.routesWithApiEndpoint}
              selectedTables={props.selectedTables}
              tree={props.tree}
            />
          </li>
        )}
        {Array.isArray(item.urlList) && item.urlList.length > 0 && (
          <li className='pl-2.5'>
            <ul className='device-view-setup-tables__tree-list'>
              {item.urlList.map((pageItem) => {
                if (!isPageItem(pageItem)) {
                  return null;
                }
                if (!props.routesWithApiEndpoint[pageItem.url]) {
                  return null;
                }
                if (!pageItem.path) {
                  return null;
                }
                return (
                  <DraggableTreeItem
                    key={pageItem.url}
                    msg={pageItem.msg}
                    onAddTable={props.onAddTable}
                    path={pageItem.path}
                    selectedTables={props.selectedTables}
                    webEndpoint={pageItem.url}
                  />
                );
              })}
            </ul>
          </li>
        )}
      </Fragment>
    );
  });

  return <ul className='device-view-setup-tables__tree-list'>{rows}</ul>;
};

interface TreeProps {
  data: MenuTreeNode[];
  onAddTable: (data: SelectedTables) => void;
  routesWithApiEndpoint: RoutesWithApiEndpoint;
  selectedTables: SelectedTables;
}

const BareDeviceViewSetupTablesTree: FC<TreeProps> = (props) => {
  const [open, setOpen] = useState<Open>({});
  return (
    <TreeRow
      data={props.data}
      onAddTable={props.onAddTable}
      onOpenChange={setOpen}
      open={open}
      routesWithApiEndpoint={props.routesWithApiEndpoint}
      selectedTables={props.selectedTables}
      tree={props.data}
    />
  );
};

export const DeviceViewSetupTablesTree = memo(BareDeviceViewSetupTablesTree);
